// Fax — 3CX-shape unified list. Toolbar: + Add (dropdown Forward/Extension)
// / Edit / Delete / Fax Server. Search. Columns: ☐ | Extension | DIDs |
// Default Caller ID. Two modal flavours: Fax Forward (DID → email, no SIP
// reg) and Fax Extension (real SIP-registered fax line for HT802 ATAs).

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Server, Search, Eye, EyeOff, ChevronDown } from 'lucide-react';
import { api, getToken } from '../lib/api';
import { useToast } from '../components/Toast';

type FaxRow = {
  fax_uuid: string;
  extension: string;
  name?: string;
  forward_to_email?: string;
  default_caller_id?: string;
  is_fax_server?: boolean;
  auth_id?: string;
  auth_password?: string;
  dids?: string[];
  kind?: 'forward' | 'extension';
};

type ModalKind = null | 'forward' | 'extension';

export default function Fax() {
  const qc    = useQueryClient();
  const toast = useToast();

  const list  = (useQuery<any[]>({ queryKey: ['fax'], queryFn: () => api.fax.list() }).data || []) as FaxRow[];

  const [search, setSearch]       = useState('');
  const [selected, setSelected]   = useState<Set<string>>(new Set());
  const [addOpen, setAddOpen]     = useState(false);
  const [modalKind, setModalKind] = useState<ModalKind>(null);
  const [editing, setEditing]     = useState<FaxRow | null>(null);

  // Each row gets a kind based on whether it has a forward_to_email set.
  const rows: FaxRow[] = list.map(r => ({
    ...r,
    kind: r.forward_to_email ? 'forward' : 'extension',
  }));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r =>
      !q || `${r.extension} ${r.name || ''} ${r.forward_to_email || ''} ${(r.dids || []).join(' ')} ${r.default_caller_id || ''}`.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const remove = useMutation({
    mutationFn: (ids: string[]) => Promise.all(ids.map(id => api.fax.remove(id))),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['fax'] }); setSelected(new Set()); },
    onError: (e: any) => toast('error', String(e.message || e)),
  });

  function toggle(id: string) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  }
  function toggleAll() {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(r => r.fax_uuid)));
  }

  function openEdit() {
    if (selected.size !== 1) return alert('Select exactly one row to edit.');
    const row = filtered.find(r => r.fax_uuid === [...selected][0]);
    if (!row) return;
    setEditing(row);
    setModalKind(row.kind || 'extension');
  }

  function openFaxServer() {
    if (selected.size !== 1) return alert('Select one row to mark as the central Fax Server.');
    const id = [...selected][0];
    if (!confirm('Designate this fax extension as the system-wide Fax Server (T.38 endpoint)?')) return;
    fetch(`/api/fax/${id}/set-server`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}` },
    }).then(() => qc.invalidateQueries({ queryKey: ['fax'] }))
      .catch((e) => toast('error', String(e.message || e)));
  }

  return (
    <div className="space-y-3">
      <h1 className="text-xl font-semibold">Fax</h1>

      {/* Toolbar — 3CX shape */}
      <div className="card p-2 flex flex-wrap gap-1 items-center relative">
        <div className="relative inline-block">
          <button className="btn-primary text-sm inline-flex items-center gap-1"
                  onClick={() => setAddOpen(o => !o)}>
            <Plus size={14} /> Add <ChevronDown size={12} />
          </button>
          {addOpen && (
            <div className="absolute z-20 mt-1 card py-1 min-w-[200px]">
              <button className="block w-full text-left text-sm px-3 py-2 hover:bg-elevated"
                      onClick={() => { setAddOpen(false); setEditing(null); setModalKind('forward'); }}>
                Create Fax Forward
              </button>
              <button className="block w-full text-left text-sm px-3 py-2 hover:bg-elevated"
                      onClick={() => { setAddOpen(false); setEditing(null); setModalKind('extension'); }}>
                Create Fax Extension
              </button>
            </div>
          )}
        </div>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={openEdit}>
          <Edit2 size={14} /> Edit
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1"
                onClick={() => {
                  if (!selected.size) return alert('Select rows to delete.');
                  if (confirm(`Delete ${selected.size} fax row(s)?`)) remove.mutate([...selected]);
                }}>
          <Trash2 size={14} /> Delete
        </button>
        <button className="btn-primary text-sm inline-flex items-center gap-1" onClick={openFaxServer}>
          <Server size={14} /> Fax Server
        </button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-1 bg-elevated rounded-md px-2 py-1.5">
        <Search size={14} className="text-textSecondary" />
        <input className="bg-transparent outline-none text-sm w-full"
               value={search} onChange={e => setSearch(e.target.value)}
               placeholder="Search…" />
      </div>

      {/* Table */}
      <div className="card p-0 overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th className="w-8">
                <input type="checkbox"
                       checked={filtered.length > 0 && selected.size === filtered.length}
                       onChange={toggleAll} />
              </th>
              <th>Extension</th>
              <th>DIDs</th>
              <th>Default Caller ID</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.fax_uuid}
                  className={`hover:bg-elevated cursor-pointer ${selected.has(r.fax_uuid) ? 'bg-primary/5' : ''}`}
                  onClick={() => { setEditing(r); setModalKind(r.kind || 'extension'); }}>
                <td onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.has(r.fax_uuid)} onChange={() => toggle(r.fax_uuid)} />
                </td>
                <td className="font-mono text-sm">
                  {r.extension}
                  <span className="text-xs text-textSecondary ml-2">
                    ({r.kind === 'forward' ? 'Fax forward' : 'Fax extension'})
                  </span>
                  {r.is_fax_server && <span className="badge bg-success/20 text-success ml-2">server</span>}
                </td>
                <td className="font-mono text-sm">
                  {(r.dids && r.dids.length > 0) ? r.dids.join(', ')
                    : (r.kind === 'extension' ? <span className="text-textSecondary">N/C</span> : '')}
                  {r.name && <span className="text-xs text-textSecondary ml-2">({r.name})</span>}
                </td>
                <td className="font-mono text-sm">{r.default_caller_id || ''}</td>
              </tr>
            ))}
            {!filtered.length && (
              <tr><td colSpan={4} className="text-center py-8 text-textSecondary">No fax rules.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modalKind === 'forward'   && <FaxForwardModal   row={editing} onClose={() => { setModalKind(null); setEditing(null); }} />}
      {modalKind === 'extension' && <FaxExtensionModal row={editing} onClose={() => { setModalKind(null); setEditing(null); }} />}
    </div>
  );
}

// ─────────────────────────────────────── Fax Forward modal ────────────────────

function FaxForwardModal({ row, onClose }: { row: FaxRow | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();

  const [name,      setName]      = useState(row?.name || '');
  const [didNumber, setDidNumber] = useState((row?.dids && row.dids[0]) || '');
  const [email,     setEmail]     = useState(row?.forward_to_email || '');
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState('');

  const dids  = useQuery<any[]>({
    queryKey: ['unassigned-dids', 'forward'],
    queryFn:  () => fetch('/api/dids?unassigned=1', { headers: { Authorization: `Bearer ${getToken()}` } }).then(r => r.json()).catch(() => []),
  }).data || [];
  const users = useQuery<any[]>({ queryKey: ['users-list'], queryFn: () => api.users.list() }).data || [];

  async function go() {
    if (!didNumber) { setErr('DID Number required'); return; }
    if (!email)     { setErr('Forwarding email required'); return; }
    setBusy(true); setErr('');
    try {
      const payload: any = { name, dids: [didNumber], forward_to_email: email, kind: 'forward' };
      if (row) await api.fax.update(row.fax_uuid, payload);
      else      await api.fax.create(payload);
      qc.invalidateQueries({ queryKey: ['fax'] });
      onClose();
    } catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="card p-4 w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-3">{row ? 'Edit Fax Forward' : 'Create Fax Forward'}</h2>
        <div className="space-y-3">
          <Field label="Name">
            <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Garys Auto Fax" />
          </Field>
          <Field label="DID Number *">
            <select className="input" value={didNumber} onChange={e => setDidNumber(e.target.value)}>
              <option value="">— Choose —</option>
              {dids.map((d: any) => (
                <option key={d.destination_uuid || d.did} value={d.destination_number || d.did}>
                  {d.destination_number || d.did}
                </option>
              ))}
              {row?.dids?.[0] && !dids.some((d: any) => (d.destination_number || d.did) === row.dids![0]) && (
                <option value={row.dids[0]}>{row.dids[0]} (current)</option>
              )}
            </select>
          </Field>
          <Field label="Send faxes to this user's email">
            <input className="input" list="fax-fwd-users" value={email}
                   onChange={e => setEmail(e.target.value)} placeholder="Enter name or email…" />
            <datalist id="fax-fwd-users">
              {users.map((u: any) => (
                <option key={u.user_uuid || u.extension_uuid} value={u.user_email || u.email || ''}>
                  {u.first_name || ''} {u.last_name || ''} ({u.extension})
                </option>
              ))}
            </datalist>
          </Field>
        </div>
        {err && <div className="text-sm text-danger mt-2">{err}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy} onClick={go}>{busy ? 'Saving…' : 'OK'}</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────── Fax Extension modal ──────────────────

function FaxExtensionModal({ row, onClose }: { row: FaxRow | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();

  const [extension, setExtension] = useState(row?.extension || '');
  const [authId,    setAuthId]    = useState(row?.auth_id || '');
  const [authPw,    setAuthPw]    = useState(row?.auth_password || '');
  const [showPw,    setShowPw]    = useState(false);
  const [callerId,  setCallerId]  = useState(row?.default_caller_id || '');
  const [didList,   setDidList]   = useState<string[]>(row?.dids || []);
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState('');

  // On create: pull initial extension/auth_id/auth_password from server
  useEffect(() => {
    if (!row && !extension) {
      api.fax.init().then(d => {
        setExtension(d.extension);
        setAuthId(d.auth_id);
        setAuthPw(d.auth_password);
      }).catch(() => {});
    }
  }, [row]);

  const allDids = useQuery<any[]>({
    queryKey: ['unassigned-dids', row?.fax_uuid],
    queryFn:  () => fetch(`/api/dids?unassigned=1${row?.fax_uuid ? `&include=${row.fax_uuid}` : ''}`, { headers: { Authorization: `Bearer ${getToken()}` } }).then(r => r.json()).catch(() => []),
  }).data || [];

  async function go() {
    if (!extension) { setErr('Fax Extension Number required'); return; }
    if (!authId)    { setErr('Auth ID required'); return; }
    if (!authPw)    { setErr('Auth Password required'); return; }
    setBusy(true); setErr('');
    try {
      const payload: any = {
        extension,
        auth_id: authId,
        auth_password: authPw,
        default_caller_id: callerId,
        dids: didList,
        kind: 'extension',
      };
      if (row) await api.fax.update(row.fax_uuid, payload);
      else      await api.fax.create(payload);
      qc.invalidateQueries({ queryKey: ['fax'] });
      onClose();
    } catch (e: any) { setErr(String(e.message || e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={onClose}>
      <div className="card p-4 w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-3">{row ? 'Edit Fax Extension' : 'Create Fax Extension'}</h2>
        <div className="space-y-3">
          <Field label="Fax Extension Number *">
            <input className="input font-mono" value={extension} onChange={e => setExtension(e.target.value)} />
          </Field>
          <Field label="Fax Extension Authentication ID *">
            <input className="input font-mono" value={authId} onChange={e => setAuthId(e.target.value)} />
          </Field>
          <Field label="Fax Extension Authentication Password *">
            <div className="relative">
              <input className="input font-mono pr-10" type={showPw ? 'text' : 'password'}
                     value={authPw} onChange={e => setAuthPw(e.target.value)} />
              <button className="absolute right-2 top-1/2 -translate-y-1/2 text-textSecondary"
                      onClick={() => setShowPw(s => !s)} title={showPw ? 'Hide' : 'Show'}>
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </Field>
          <Field label="Default Caller ID">
            <input className="input font-mono" value={callerId} onChange={e => setCallerId(e.target.value)} placeholder="+1989…" />
          </Field>
          <Field label="Assigned DID number(s)">
            <select className="input" multiple size={5} value={didList}
                    onChange={e => setDidList(Array.from(e.target.selectedOptions, o => o.value))}>
              {allDids.map((d: any) => (
                <option key={d.destination_uuid || d.did} value={d.destination_number || d.did}>
                  {d.destination_number || d.did}
                </option>
              ))}
            </select>
            <div className="text-xs text-textSecondary mt-1">Hold Ctrl/Cmd to multi-select.</div>
          </Field>
        </div>
        {err && <div className="text-sm text-danger mt-2">{err}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy} onClick={go}>{busy ? 'Saving…' : 'OK'}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return (
    <div>
      <label className="block text-sm text-textSecondary mb-1">{label}</label>
      {children}
    </div>
  );
}
